import { slugifyPath } from "@certn/utils";
import { ControlProps } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import {
  ComponentType,
  createContext,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useTranslation } from "react-i18next";

import { CheckResultsNullableWrapper } from "./CheckResultsNullableWrapper";
import { CheckResultsOptionalWrapper } from "./CheckResultsOptionalWrapper";
import { DebugWrapper } from "./DebugWrapper";
import { CascadiaControlProps, CascadiaControlOptions } from "./types";
import { useLayoutProps } from "./withCascadiaLayoutProps";
import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";

export type { CascadiaControlProps, CascadiaControlOptions };

export const ControlContext = createContext<CascadiaControlProps>(undefined!);

export function withCascadiaControlProps(
  Component: ComponentType<CascadiaControlProps>,
  options: CascadiaControlOptions = {}
) {
  return withJsonFormsControlProps((props: ControlProps) => {
    const { t } = useTranslation();

    const parentLayout = useLayoutProps();

    const form = useContext(CascadiaJsonFormContext);

    const label =
      props.uischema.options?.fieldErrorLabel || props.label || t("Field");

    const errors = useMemo(() => {
      let errors: string[] = [];

      const ajvErrors = props.errors.split("\n");
      const submitErrors = form.submitErrors[props.path];

      if (ajvErrors.length > 0) {
        errors = errors.concat(
          ajvErrors.filter(Boolean).map((err) => [label, err].join(" "))
        );
      }

      const isEmptyRequiredValue =
        props.data === undefined ||
        props.data === "" ||
        (options.treatNullAsEmpty && props.data === null);

      if (isEmptyRequiredValue && props.required) {
        errors.push(t("{{label}} is a required field", { label }));
      }

      if (Array.isArray(submitErrors)) {
        errors = errors.concat(submitErrors);
      } else if (typeof submitErrors === "string") {
        errors.push(submitErrors);
      }

      const uniqueErrors = [...new Set(errors)];

      // "error_message" is supposed to override any other error messages, if provided
      if (uniqueErrors.length && props.uischema.options?.error_message) {
        return [props.uischema.options.error_message];
      }
      return uniqueErrors;
    }, [
      props.data,
      props.errors,
      props.required,
      form.submitErrors[props.path],
      options.treatNullAsEmpty,
    ]);

    const touched = useMemo(() => {
      if (form.touched) {
        return Boolean(form.touched[props.path] || form.isSubmitted);
      }

      return true;
    }, [options.alwaysShowErrors, form.touched, form.isSubmitted]);

    const cascadiaProps = useMemo(
      () => ({
        touched,
        invalid: touched && errors.length > 0,
        options: props.uischema.options || {},
        description: props.uischema.options?.description || props.description,
        placeholder: props.uischema.options?.placeholder,
        errors:
          options.alwaysShowErrors ||
          (!parentLayout.stopProcessingErrors && touched)
            ? errors
            : [],
        required: props.required || props.uischema.options?.is_required,
        setValue(value: unknown) {
          props.handleChange(props.path, value);
        },
        setTouched() {
          form.setTouched(props.path);
          parentLayout.setTouched();
        },
      }),
      [
        props.path,
        parentLayout.stopProcessingErrors,
        touched,
        form.setTouched,
        errors,
      ]
    );

    useEffect(() => {
      if (
        props.path &&
        (parentLayout.visible === false || props.visible === false)
      ) {
        props.handleChange(props.path, props?.schema?.default);
      }
    }, [props.visible]);

    useEffect(() => {
      const invisible =
        parentLayout.visible === false || props.visible === false;

      if (options.manuallySetFieldStatus) {
        return;
      }

      form.setFieldErrors(props.path, invisible ? [] : errors);

      return () => {
        if (options.manuallySetFieldStatus) {
          return;
        }

        form.setFieldErrors(props.path, []);
      };
    }, [props.path, errors, props.visible, parentLayout.visible]);

    // Use display: none to hide the control, but still render it
    const hidden = props.uischema.options?.hidden === true;

    const testID = useMemo(
      () => `form--${Component.displayName}--${slugifyPath(props.path)}`,
      [Component.displayName, props.path]
    );

    // don't render the control if the "hide" rule was triggered
    if (parentLayout.visible === false || props.visible === false) {
      return null;
    }

    return (
      <ControlContext.Provider value={{ ...props, ...cascadiaProps, testID }}>
        <CheckResultsOptionalWrapper
          isCheckResults={form.isCheckResults}
          schema={parentLayout.schema}
          readonly={form.readonly}
          hidden={hidden}
          label={props.label}
          data={props.data}
          path={props.path}
          setValues={form.setValues}
          values={form.values}
        >
          <CheckResultsNullableWrapper
            isCheckResults={form.isCheckResults}
            schema={props.schema}
            readonly={form.readonly}
            hidden={hidden}
            label={props.label}
            data={props.data}
            setValue={cascadiaProps.setValue}
          >
            <DebugWrapper
              Component={Component}
              componentProps={{ ...props, ...cascadiaProps, testID }}
              type="control"
              hidden={hidden}
            />
          </CheckResultsNullableWrapper>
        </CheckResultsOptionalWrapper>
      </ControlContext.Provider>
    );
  });
}

export function useControlProps() {
  return useContext(ControlContext);
}
